import { DASHBOARD_ROUTES } from "../homeDashboard/dashboardRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../homeDashboard/HomeDashboardLayout";
import { BackSVG, LanguageMobileIcon } from "../components/SvgIcons";
import ShieldIcon from "../assets/images/lect5.png";
import "../dashboard/Dashboard.css";
import "./Notification.css";
import { useNotifications } from "../hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import MyWalletSidebar from "../homeDashboard/MyWalletSidebar";
import { useSidebarData } from "../hooks/useSidebarData";
import { getStoredUser } from "../utils/authStorage";
import { markNotificationSeen } from "../services/notificationService";
import { resolveNotificationDestination } from "./notificationRouting";

function toNumber(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function resolveUnreadStatus(item) {
  const directUnread = item?.unread ?? item?.is_unread ?? item?.isUnread;
  if (directUnread !== undefined) {
    return Boolean(toNumber(directUnread) ?? directUnread);
  }

  const seenStatus = item?.seen_status ?? item?.seenStatus;
  if (seenStatus !== undefined) {
    const normalizedSeenStatus = toNumber(seenStatus);
    if (normalizedSeenStatus !== null) {
      return normalizedSeenStatus === 0;
    }
  }

  const readValue = item?.is_read ?? item?.read ?? item?.isRead ?? item?.read_status;
  if (readValue !== undefined) {
    return !(toNumber(readValue) ?? Number(Boolean(readValue)));
  }

  return false;
}

function resolveDateValue(item) {
  return item?.created_at || item?.notification_date || item?.date || item?.createdAt || item?.updated_at || "";
}

function normalizeDateInput(dateValue) {
  if (typeof dateValue !== "string") {
    return dateValue;
  }

  return dateValue.includes(" ") ? dateValue.replace(" ", "T") : dateValue;
}

function formatNotificationDate(dateValue) {
  if (!dateValue) {
    return "Recent";
  }

  const parsedDate = new Date(normalizeDateInput(dateValue));
  if (Number.isNaN(parsedDate.getTime())) {
    return String(dateValue);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(parsedDate);
}

function formatNotificationTime(dateValue) {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(normalizeDateInput(dateValue));
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsedDate);
}

function formatNotificationAgo(dateValue, unread) {
  if (!dateValue) {
    return unread ? "Unread" : "Read";
  }

  const parsedDate = new Date(normalizeDateInput(dateValue));
  if (Number.isNaN(parsedDate.getTime())) {
    return unread ? "Unread" : "Read";
  }

  const diffMs = Date.now() - parsedDate.getTime();
  if (diffMs <= 0) {
    return "Just now";
  }

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (diffMs < hour) {
    const minutes = Math.max(1, Math.floor(diffMs / minute));
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  if (diffMs < day) {
    const hours = Math.floor(diffMs / hour);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  if (diffMs < week) {
    const days = Math.floor(diffMs / day);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  const weeks = Math.floor(diffMs / week);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

function normalizeNotificationItem(item, index) {
  const unread = resolveUnreadStatus(item);
  const dateValue = resolveDateValue(item);

  return {
    id: item?.notification_log_id || item?.notificationLogId || item?.id || item?.notification_id || item?.notifications_id || `notification-${index}`,
    logId: item?.notification_log_id || item?.notificationLogId || 0,
    raw: item,
    title: item?.title || item?.heading || item?.subject || item?.name || "Notification",
    message: item?.message || item?.description || item?.body || item?.content || "No notification message available.",
    image: item?.image_url || item?.image || item?.icon || ShieldIcon,
    date: formatNotificationDate(dateValue),
    time: formatNotificationTime(dateValue),
    ago: item?.relative_time || formatNotificationAgo(dateValue, unread),
    unread,
    accentBackground: item?.background_color ? `#${String(item.background_color).replace(/^#/, "")}` : "",
    accentBorder: item?.border_color ? `#${String(item.border_color).replace(/^#/, "")}` : "",
    category: item?.quest_category_name || item?.category_name || "",
  };
}

const NotificationSkeleton = () => (
  <div className="notification-list" aria-hidden="true">
    {Array.from({ length: 4 }).map((_, index) => (
      <article className="notification-card" key={index}>
        <div className="notification-card__body">
          <div className="notification-card__header">
            <div className="notification-card__icon-wrap">
              <span className="dashboard-skeleton dashboard-skeleton--icon notification-card__icon" />
            </div>

            <div className="dashboard-skeleton-stack" style={{ flex: 1 }}>
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title" />
            </div>
          </div>

          <div className="dashboard-skeleton-stack">
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text" />
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short" />
          </div>

          <div className="dashboard-skeleton-stack" style={{ marginTop: "16px" }}>
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text" />
          </div>
        </div>
      </article>
    ))}
  </div>
);

const MAX_VISIBLE_PAGES = 5;

function buildVisiblePages(currentPage, totalPages) {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(MAX_VISIBLE_PAGES / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + MAX_VISIBLE_PAGES - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - MAX_VISIBLE_PAGES + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

const Notification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: sidebarData } = useSidebarData();
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);
  const {
    data: paginatedNotifications,
    isLoading,
    isError,
    error,
  } = useNotifications({
    page: currentPage,
    unreadOnly: activeTab === "unread",
  });
  const { data: allNotificationsMeta } = useNotifications({
    page: 1,
    unreadOnly: false,
  });
  const { data: unreadNotificationsMeta } = useNotifications({
    page: 1,
    unreadOnly: true,
  });

  const pagination = paginatedNotifications?.pagination ?? {
    currentPage: 1,
    perPage: 0,
    totalRecords: 0,
    totalPages: 1,
  };

  const normalizedNotifications = useMemo(
    () => (Array.isArray(paginatedNotifications?.items) ? paginatedNotifications.items : []).map(normalizeNotificationItem),
    [paginatedNotifications?.items]
  );

  const notificationCounts = useMemo(
    () => ({
      all: allNotificationsMeta?.pagination?.totalRecords || 0,
      unread: unreadNotificationsMeta?.pagination?.totalRecords || 0,
    }),
    [allNotificationsMeta, unreadNotificationsMeta]
  );

  const errorMessage = isError && normalizedNotifications.length === 0
    ? error?.response?.data?.message || error?.message || "Unable to load notifications."
    : "";
  const visiblePages = buildVisiblePages(pagination.currentPage, pagination.totalPages);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    queryClient.invalidateQueries({
      queryKey: ['notifications', { page: 1, unreadOnly: tabId === 'unread' }],
      exact: true,
    });
  };

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(DASHBOARD_ROUTES.home, { replace: true });
  };

  const handleNotificationClick = (item) => {
    if (item.unread && item.logId) {
      const matches = (row) => String(row?.notification_log_id || row?.notificationLogId) === String(item.logId);
      queryClient.getQueriesData({ queryKey: ['notifications'] }).forEach(([key, data]) => {
        if (!data?.items) return;
        const unreadOnly = Boolean(key[1]?.unreadOnly);
        const items = unreadOnly
          ? data.items.filter((row) => !matches(row))
          : data.items.map((row) => matches(row) ? { ...row, seen_status: 1, unread: false } : row);
        queryClient.setQueryData(key, {
          ...data,
          items,
          pagination: {
            ...data.pagination,
            totalRecords: Math.max(0, (data.pagination?.totalRecords || 0) - (unreadOnly ? 1 : 0)),
          },
        });
      });
      markNotificationSeen(item.logId)
        .catch(() => {})
        .finally(() => queryClient.invalidateQueries({ queryKey: ['notifications'] }));
    }

    const profile = sidebarData?.user_data || getStoredUser() || {};
    const selectedState = sidebarData?.selected_state || {};
    const destination = resolveNotificationDestination(item.raw, {
      savedStateId: selectedState.state_id || profile.state_id || profile.user_state_id,
      savedStateName: selectedState.state_name || profile.state_name || profile.user_state_name,
      origin: window.location.origin,
    });
    if (destination.kind === 'external') {
      window.open(destination.url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(destination.path, { state: destination.state });
  };

  return (
    <>
      <style>{`
        .page-content {
          background: #fff;
        }

        body {
          background: #fff !important;
        }

        @media (max-width: 767px) {
          .headerDash {
            display: none;
          }

          .layout-wrapper .main-content {
            padding-top: 0px !important;
          }
        }
      `}</style>

      <Layout showHeader={false}>
        <div className="d-flex justify-content-between align-items-center d-xl-none header-Mobile subject-podcast notif-header">
          <button
            type="button"
            className="languageImg d-flex d-xl-none border-0 bg-transparent p-0"
            onClick={handleBackNavigation}
            aria-label="Go back"
          >
            <BackSVG />
          </button>

          <h4 className="fw-bold mb-0"></h4>

          <div className="languageImg d-flex d-xl-none">
            <LanguageMobileIcon />
          </div>
        </div>

        <div className="qb-wrapper notification-page pageHeight">
          <div className="notification-page__shell">
            {/* <section className="notification-hero">
                <div className="d-flex align-items-center notification-head">
              <div className="notification-hero__icon">
                <i className="bi bi-bell-fill"></i>
              </div>
              <div className="notification-hero__content">
                <h1 className="notification-hero__title blackText">Notifications</h1>
              </div>
                </div>
               <p className="notification-hero__text">
                  Stay updated with every important activity, reminder, and learning alert in one place.
                </p>
            
            </section> */}
            <div>
             <h1 class="qb-title">Notifications </h1>
             <p class="qb-sub"> Stay updated with every important activity, reminder, and learning alert
               in one place.</p>
           </div>

            <div className="notification-tabs" role="tablist" aria-label="Notification filters">
              {[
                { id: "all", label: "All", count: notificationCounts.all },
                { id: "unread", label: "Unread", count: notificationCounts.unread },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`notification-tab${activeTab === tab.id ? " is-active" : ""}`}
                  onClick={() => handleTabChange(tab.id)}
                  aria-pressed={activeTab === tab.id}
                >
                  <span>{tab.label}</span>
                  <span className="notification-tab__count">{tab.count}</span>
                </button>
              ))}
            </div>

            {isLoading ? <NotificationSkeleton /> : null}
            {!isLoading && errorMessage ? (
              <div className="notification-list">
                <article className="notification-card">
                  <div className="notification-card__body">
                    <p className="notification-card__message mb-0">{errorMessage}</p>
                  </div>
                </article>
              </div>
            ) : null}
            {!isLoading && !errorMessage ? (
              <div className="notification-list">
                {normalizedNotifications.length > 0 ? normalizedNotifications.map((item) => (
                  <article
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleNotificationClick(item);
                      }
                    }}
                    className={`notification-card${item.unread ? " notification-card--unread" : ""}`}
                    style={{
                      borderColor: item.accentBorder || undefined,
                      background: item.unread && item.accentBackground
                        ? `linear-gradient(180deg, #ffffff 0%, ${item.accentBackground}22 100%)`
                        : undefined,
                    }}
                  >
                    <div className="notification-card__body">
                      <div className="notification-card__header">
                        <div className="notification-card__icon-wrap">
                          <div className="notification-card__icon" aria-hidden="true">
                            <img src={item.image} alt="" />
                          </div>

                          {item.unread && (
                            <span
                              className="notification-card__dot notification-card__dot--unread"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <div className="notification-card__heading">
                          <div className="notification-card__title-row">
                            <div className="d-flex align-items-center justify-content-between w-100">
                            <h2 className="notification-card__title">{item.title}</h2>
                               <span
                              className="notification-card__badge"
                              style={{
                                backgroundColor: item.accentBackground || undefined,
                                border: item.accentBorder ? `1px solid ${item.accentBorder}` : undefined,
                              }}
                            >
                              {item.category || (item.unread ? "Unread" : "Read")}
                            </span>
                            
                            </div>
                         
                          </div>
                            <p className="notification-card__message">{item.message}</p>
                        </div>
                      </div>

                      {/* <p className="notification-card__message">{item.message}</p> */}

                      <div className="notification-card__meta">
                        <div className="notification-card__meta-group">
                          <div className="notification-card__meta-item">
                            <i className="bi bi-calendar"></i>
                            <span>{item.date}</span>
                          </div>
                        </div>
                        <div className="notification-card__meta-group">
                          <div className="notification-card__meta-item">
                            <i className="bi bi-clock"></i>
                            <span>{item.time || " "}</span>
                          </div>
                        </div>
                        <div className="notification-card__ago">{item.ago}</div>
                      </div>
                    </div>
                  </article>
                )) : (
                  <article className="notification-card">
                    <div className="notification-empty">
                      <h3 className="notification-empty__title">
                        {activeTab === "unread" ? "No unread notifications" : "No notifications"}
                      </h3>
                      <p className="notification-empty__text">
                        {activeTab === "unread"
                          ? "Everything is caught up right now."
                          : "Notifications will appear here when new activity arrives."}
                      </p>
                    </div>
                  </article>
                )}
              </div>
            ) : null}

            {!isLoading && !errorMessage && pagination.totalPages > 1 ? (
              <div className="notification-pagination">
                <button
                  type="button"
                  className="notification-pagination__nav"
                  disabled={pagination.currentPage === 1}
                  onClick={() => setCurrentPage((previousPage) => Math.max(1, previousPage - 1))}
                >
                  Previous
                </button>

                <div className="notification-pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`notification-pagination__page${pageNumber === pagination.currentPage ? " is-active" : ""}`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="notification-pagination__nav"
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => setCurrentPage((previousPage) => Math.min(pagination.totalPages, previousPage + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <MyWalletSidebar withTopOffset={false} />
      </Layout>
    </>
  );
};

export default Notification;
